import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
import {
  BlogCategoryContent,
  BlogContent,
  BlogPost,
  BlogPostDetail,
  BlogTopic
} from '@app/model/blog-content.model';
import { forkJoin, Observable, throwError, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TranslationService } from '@app/core/_base/layout';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface ApiCollection<T> {
  collection: T[];
  total: number;
}

// interface ApiBlogCategoryCollection {
//   collection: ApiBlogCategory[];
//   total: number;
// }

interface ApiBlogCategoryPost {
  id: string;
  title: string;
  order: number;
}

interface ApiBlogCategoryChild {
  id: string;
  name: string;
}

interface ApiBlogCategory {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  code: string;
  priority: number | null;
  is_hide: boolean;

  img?: string | null;
  image?: string | null;
  description?: string | null;

  total_blog?: number;
  blog_posts?: ApiBlogCategoryPost[];

  category_child_total?: number;
  category_child?: ApiBlogCategoryChild[];
}

interface ApiBlogPostReference {
  id: string;
  slug: string;
}

interface ApiBlogCategoryDetail {
  blog_post: ApiBlogPostReference[];
}

interface ApiBlogPostTranslation {
  language_code: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
}

interface ApiBlogPostCategory {
  id: string;
  name: string;
}

interface ApiBlogPost {
  id: string;

  category_id?: string;
  category?: ApiBlogPostCategory;

  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image: string | null;
  author: string | null;

  is_published: boolean;
  published_at: string | null;
  created_at: string;
  created_by: string | null;

  translations?: ApiBlogPostTranslation[];

  category_detail?: ApiBlogCategoryDetail;
}

@Injectable({
  providedIn: 'root'
})
export class BlogContentDataService {
  private readonly apiUrl = environment.backendhost + '/api/blog';

  constructor(
    private http: HttpClient,
    private translationService: TranslationService
  ) {}
  //   getAllPosts(): Observable<BlogPost[]> {
  //   return this.http
  //     .get<ApiResponse<ApiCollection<ApiBlogPost>>>(
  //       this.apiUrl + '/posts?language=vi'
  //     )
  //     .pipe(
  //       map((response) =>
  //         response.data.collection
  //           .slice()
  //           .sort(
  //             (firstPost, secondPost) =>
  //               this.getPostTimestamp(secondPost) -
  //               this.getPostTimestamp(firstPost)
  //           )
  //           .map((post) => this.mapPost(post, language))
  //       )
  //     );
  // }
  getAllPosts(): Observable<BlogPost[]> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) => this.loadAllPosts(language))
    );
  }

  private loadAllPosts(language: string): Observable<BlogPost[]> {
    const languageQuery = encodeURIComponent(language);

    return this.http
      .get<
        ApiResponse<ApiCollection<ApiBlogPost>>
      >(this.apiUrl + '/posts?language=' + languageQuery + '&is_published=true' + '&Page=1&PageSize=9999')
      .pipe(
        map((response) =>
          response.data.collection
            .filter((post) => post.is_published === true)
            .sort(
              (firstPost, secondPost) =>
                this.getPostTimestamp(secondPost) - this.getPostTimestamp(firstPost)
            )
            .map((post) => this.mapPost(post, language))
        )
      );
  }

  getCategoryContent(parentSlug: string, childSlug?: string): Observable<BlogCategoryContent> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) => {
        const languageQuery = encodeURIComponent(language);

        return this.loadCategoriesWithFallback(language).pipe(
          switchMap((categories) => {
            const parentCategory = categories.find(
              (category) =>
                category.slug === parentSlug &&
                category.parent_id === null &&
                category.is_hide !== true
            );

            if (!parentCategory) {
              throw new Error('Parent category not found');
            }

            let selectedCategory = parentCategory;

            if (childSlug) {
              const childCategory = categories.find(
                (category) =>
                  category.slug === childSlug &&
                  category.parent_id === parentCategory.id &&
                  category.is_hide !== true
              );

              if (!childCategory) {
                throw new Error('Child category not found');
              }

              selectedCategory = childCategory;
            }

            const childCategories = childSlug
              ? []
              : categories
                  .filter(
                    (category) =>
                      category.parent_id === parentCategory.id && category.is_hide !== true
                  )
                  .sort(
                    (firstCategory, secondCategory) =>
                      (firstCategory.priority || 0) - (secondCategory.priority || 0)
                  );

            const postsUrl =
              this.apiUrl +
              '/posts?category=' +
              encodeURIComponent(selectedCategory.slug) +
              '&language=' +
              languageQuery +
              '&is_published=true' +
              '&Page=1&PageSize=9999';

            // return this.http.get<ApiResponse<ApiCollection<ApiBlogPost>>>(postsUrl).pipe(
            //   map((postsResponse) => ({
            //     category: this.mapTopic(selectedCategory),
            //     parentCategory: childSlug ? this.mapTopic(parentCategory) : null,
            //     childCategories: childCategories.map((category) => this.mapTopic(category)),
            //     posts: postsResponse.data.collection
            //       .filter((post) => post.is_published === true)
            //       .sort(
            //         (firstPost, secondPost) =>
            //           this.getPostTimestamp(secondPost) - this.getPostTimestamp(firstPost)
            //       )
            //       .map((post) => this.mapPost(post, language))
            //   }))
            // );
            return this.http.get<ApiResponse<ApiCollection<ApiBlogPost>>>(postsUrl).pipe(
              map((postsResponse) => {
                const posts =
                  postsResponse &&
                  postsResponse.data &&
                  Array.isArray(postsResponse.data.collection)
                    ? postsResponse.data.collection
                    : [];

                return {
                  category: this.mapTopic(selectedCategory),

                  parentCategory: childSlug ? this.mapTopic(parentCategory) : null,

                  childCategories: childCategories.map((category) => this.mapTopic(category)),

                  posts: posts
                    .filter((post) => post.is_published === true)
                    .sort(
                      (firstPost, secondPost) =>
                        this.getPostTimestamp(secondPost) - this.getPostTimestamp(firstPost)
                    )
                    .map((post) => this.mapPost(post, language))
                };
              })
            );
          })
        );
      })
    );
  }

  getContent(): Observable<BlogContent> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) => {
        const languageQuery = encodeURIComponent(language);

        return forkJoin({
          categories: this.loadCategoriesWithFallback(language),

          latestPosts: this.http.get<ApiResponse<ApiBlogPost[]>>(
            this.apiUrl + '/posts/latest?language=' + languageQuery
          ),

          hotArticles: this.http.get<ApiResponse<ApiBlogPost[]>>(
            this.apiUrl + '/posts/hot?count=6&language=' + languageQuery
          ),

          featuredPosts: this.http.get<ApiResponse<ApiBlogPost[]>>(
            this.apiUrl + '/posts/featured?count=3&language=' + languageQuery
          )
        }).pipe(
          map((response) => ({
            topics: response.categories
              .filter((category) => category.is_hide !== true && category.parent_id === null)
              .sort(
                (firstCategory, secondCategory) =>
                  (firstCategory.priority || 0) - (secondCategory.priority || 0)
              )
              .map((category) => this.mapTopic(category)),
            latestPosts: response.latestPosts.data
              .filter((post) => post.is_published === true)
              .slice(0, 4)
              .map((post) => this.mapPost(post, language)),

            hotArticles: response.hotArticles.data
              .filter((post) => post.is_published === true)
              .map((post) => this.mapPost(post, language)),

            featuredPosts: response.featuredPosts.data
              .filter((post) => post.is_published === true)
              .map((post) => this.mapPost(post, language))
          }))
        );
      })
    );
  }

  private getPostContent(post: ApiBlogPost, languageCode: string): ApiBlogPostTranslation {
    const translations = post.translations || [];

    const selectedTranslation = translations.find(
      (translation) =>
        translation.language_code === languageCode && this.hasPostContent(translation)
    );

    if (selectedTranslation) {
      return selectedTranslation;
    }

    const englishTranslation = translations.find(
      (translation) => translation.language_code === 'en' && this.hasPostContent(translation)
    );

    if (englishTranslation) {
      return englishTranslation;
    }

    const availableTranslation = translations.find((translation) =>
      this.hasPostContent(translation)
    );

    if (availableTranslation) {
      return availableTranslation;
    }

    return {
      language_code: languageCode,
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || ''
    };
  }

  private hasPostContent(translation: ApiBlogPostTranslation): boolean {
    return !!(
      translation &&
      translation.title &&
      translation.title.trim() &&
      translation.slug &&
      translation.slug.trim() &&
      translation.content &&
      translation.content.trim()
    );
  }

  private loadPostBySlug(postSlug: string, languages: string[]): Observable<ApiBlogPost> {
    if (!languages.length) {
      return throwError(new Error('Post is unavailable'));
    }

    const language = languages[0];
    const remainingLanguages = languages.slice(1);

    const url =
      this.apiUrl +
      '/posts/' +
      encodeURIComponent(postSlug) +
      '?language=' +
      encodeURIComponent(language);

    return this.http.get<ApiResponse<ApiBlogPost>>(url).pipe(
      map((response) => {
        if (!response || !response.success || !response.data) {
          throw new Error('Post not found');
        }

        return response.data;
      }),
      catchError(() => {
        if (!remainingLanguages.length) {
          return throwError(new Error('Post is unavailable'));
        }

        return this.loadPostBySlug(postSlug, remainingLanguages);
      })
    );
  }

  private loadCategoriesWithFallback(language: string): Observable<ApiBlogCategory[]> {
    const languages = [language, 'en', 'vi', 'zh'].filter(
      (item, index, items) => items.indexOf(item) === index
    );

    const requests = languages.map((languageCode) =>
      this.http
        .get<
          ApiResponse<ApiCollection<ApiBlogCategory>>
        >(this.apiUrl + '/categories?language=' + encodeURIComponent(languageCode))
        .pipe(
          map((response) => (response && response.data ? response.data.collection || [] : [])),
          catchError(() => of([] as ApiBlogCategory[]))
        )
    );

    return forkJoin(requests).pipe(
      map((categoryCollections) => {
        const primaryCategories = categoryCollections[0] || [];

        const existingCategories =
          categoryCollections.find((categories) => categories.length > 0) || [];

        const baseCategories =
          primaryCategories.length > 0 ? primaryCategories : existingCategories;

        const fallbackCollections = categoryCollections.slice(1);

        return baseCategories.map((category) => {
          const fallbackCategory = fallbackCollections
            .map((categories) =>
              categories.find(
                (fallbackItem) =>
                  fallbackItem.id === category.id && (fallbackItem.name || '').trim().length > 0
              )
            )
            .find((fallbackItem) => !!fallbackItem);

          return {
            ...category,

            name:
              category.name && category.name.trim()
                ? category.name
                : fallbackCategory
                  ? fallbackCategory.name
                  : category.code,

            slug:
              category.slug && category.slug.trim()
                ? category.slug
                : fallbackCategory && fallbackCategory.slug
                  ? fallbackCategory.slug
                  : category.code.toLowerCase(),

            description:
              category.description && category.description.trim()
                ? category.description
                : fallbackCategory && fallbackCategory.description
                  ? fallbackCategory.description
                  : ''
          };
        });
      })
    );
  }

  getPostBySlug(postSlug: string): Observable<BlogPostDetail> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) => {
        const lookupLanguages = [language, 'en', 'vi', 'zh'].filter(
          (item, index, languages) => languages.indexOf(item) === index
        );

        return this.loadPostBySlug(postSlug, lookupLanguages).pipe(
          map((post) => {
            if (post.is_published !== true) {
              throw new Error('Post is unavailable or unpublished');
            }

            return this.mapPostDetail(post, language);
          })
        );
      })
    );
  }

  private mapPostDetail(post: ApiBlogPost, languageCode: string): BlogPostDetail {
    const content = this.getPostContent(post, languageCode);

    return {
      id: post.id,
      slug: content.slug,
      title: content.title,
      publishedAt: this.formatDate(post.published_at || post.created_at),
      excerpt: content.excerpt || '',
      content: content.content || '',
      author: post.author || '',
      createdBy: post.created_by
    };
  }

  private getPostTimestamp(post: ApiBlogPost): number {
    const postDate = post.published_at ? post.published_at : post.created_at;

    return new Date(postDate).getTime();
  }
  private mapApiLanguage(language: string): string {
    const currentLanguage = (language || '').toLowerCase();

    if (currentLanguage === 'ch' || currentLanguage === 'cn') {
      return 'zh';
    }

    if (currentLanguage === 'en') {
      return 'en';
    }

    return 'vi';
  }

  private mapTopic(category: ApiBlogCategory): BlogTopic {
    return {
      id: category.id,
      slug: category.slug,
      title: category.name || '',
      description: category.description || '',
      imageUrl: this.getImageUrl(category.img || category.image || null)
    };
  }

  private mapPost(post: ApiBlogPost, languageCode: string): BlogPost {
    const content = this.getPostContent(post, languageCode);

    return {
      id: post.id,
      categoryId: post.category_id || (post.category ? post.category.id : ''),
      title: content.title,
      slug: content.slug,
      description: content.excerpt || '',
      imageUrl: this.getImageUrl(post.featured_image),
      publishedAt: this.formatDate(post.published_at || post.created_at)
    };
  }

  private getImageUrl(imageUrl: string | null): string {
    if (!imageUrl) {
      return '';
    }

    if (imageUrl.indexOf('http') === 0) {
      return imageUrl;
    }

    return environment.backendhost + imageUrl;
  }

  private formatDate(dateValue: string): string {
    return new Date(dateValue).toLocaleDateString('vi-VN');
  }
}
